<template>
  <AltModal :open="open" title="Accès API — jetons personnels" size="lg" @update:open="(o: boolean) => emit('update:open', o)">
    <div class="space-y-5 text-sm">
      <!-- CE QUE LE JETON PERMET, dit avant qu'on en crée un : il agit EN
           VOTRE NOM. C'est la seule chose à comprendre, et la plus facile à
           oublier une fois le jeton collé dans un outil. -->
      <p class="text-slate-600 dark:text-alt-dark-muted">
        Un jeton permet à un outil — Claude, un script — de lire et de faire avancer vos tâches
        <strong>en votre nom, avec vos droits</strong>. Chaque modification qu'il fait est tracée au journal
        sous votre nom, avec celui du jeton. Révoquez-le dès qu'il ne sert plus.
      </p>

      <!-- Le secret, UNE fois. Il ne sera plus jamais relisible : on le dit,
           et on donne de quoi le coller tout de suite où il sert. -->
      <div v-if="cree" class="space-y-2 rounded-lg border-l-2 border-success bg-success/5 px-3 py-3">
        <p class="font-medium text-success">Jeton « {{ cree.apiToken.name }} » créé — copiez-le maintenant</p>
        <p class="text-xs text-slate-500 dark:text-alt-dark-muted">Il ne s'affichera plus : seule son empreinte est conservée.</p>
        <div class="flex items-center gap-2">
          <code class="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 font-mono text-xs dark:bg-alt-dark-raised">{{ cree.secret }}</code>
          <AltButton size="sm" variant="secondary" @click="copier(cree.secret)">Copier</AltButton>
        </div>
        <details class="text-xs text-slate-500 dark:text-alt-dark-muted">
          <summary class="cursor-pointer font-medium">Le brancher dans Claude</summary>
          <p class="mt-2">
            Avec le serveur MCP du dépôt (<code>outils/mcp-project-it/serveur.mjs</code>), dans la configuration
            MCP de Claude :
          </p>
          <pre class="mt-1 overflow-x-auto rounded bg-white p-2 font-mono text-[11px] dark:bg-alt-dark-raised">{{ configClaude }}</pre>
          <p class="mt-2">Ou directement en HTTP : <code>Authorization: Bearer {{ cree.apiToken.prefix }}…</code></p>
        </details>
      </div>

      <form class="flex flex-wrap items-end gap-2" @submit.prevent="creer">
        <AltFormField label="Nom du jeton" class="min-w-[14rem] flex-1" :error="erreur">
          <AltInput v-model="nom" placeholder="Ex. Claude Desktop — poste du bureau" maxlength="80" />
        </AltFormField>
        <AltFormField label="Valable">
          <AltSelect v-model="duree" :options="durees" />
        </AltFormField>
        <AltButton type="submit" :loading="enCours" :disabled="!nom.trim()">Créer le jeton</AltButton>
      </form>

      <div>
        <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Vos jetons</p>
        <p v-if="chargement" class="text-xs text-slate-400">Chargement…</p>
        <p v-else-if="!jetons.length" class="text-xs text-slate-400">Aucun jeton.</p>
        <ul v-else class="divide-y divide-slate-100 dark:divide-alt-dark-border">
          <li v-for="j in jetons" :key="j.id" class="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
            <span class="min-w-0 flex-1">
              <span class="font-medium text-space-indigo dark:text-altoneo-50">{{ j.name }}</span>
              <code class="ml-2 font-mono text-[11px] text-slate-400">{{ j.prefix }}…</code>
            </span>
            <span class="text-xs text-slate-500 dark:text-alt-dark-muted">
              {{ j.lastUsedAt ? `utilisé le ${fmtDate(j.lastUsedAt)}` : 'jamais utilisé' }} ·
              {{ j.expiresAt ? `expire le ${fmtDate(j.expiresAt)}` : 'sans expiration' }}
            </span>
            <button type="button" class="pa-geste pa-geste--danger" @click="revoquer(j)">
              <AppIcon nom="corbeille" :taille="12" /><span class="pa-geste-mot">Révoquer</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </AltModal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useToast } from 'altoneo-charting';
import type { ApiToken, ApiTokenCreated } from '@its/shared';
import { api, ApiError } from '@/lib/api';
import { confirmer } from '@/lib/confirmer';
import { fmtDate } from '@/lib/dates';
import AppIcon from '@/components/AppIcon.vue';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ 'update:open': [open: boolean] }>();

const toast = useToast();
const jetons = ref<ApiToken[]>([]);
const chargement = ref(false);
const nom = ref('');
const duree = ref('90');
const enCours = ref(false);
const erreur = ref('');
const cree = ref<ApiTokenCreated | null>(null);

const durees = [
  { value: '30', label: '30 jours' },
  { value: '90', label: '90 jours' },
  { value: '365', label: '1 an' },
  { value: 'aucune', label: 'Sans expiration' },
];

const configClaude = computed(() =>
  JSON.stringify(
    {
      mcpServers: {
        'project-it': {
          command: 'node',
          args: ['<chemin du dépôt>/outils/mcp-project-it/serveur.mjs'],
          env: { PROJECT_IT_URL: window.location.origin, PROJECT_IT_TOKEN: cree.value?.secret ?? '' },
        },
      },
    },
    null,
    2,
  ),
);

async function charger(): Promise<void> {
  chargement.value = !jetons.value.length;
  try {
    jetons.value = (await api.get<{ apiTokens: ApiToken[] }>('/me/api-tokens')).apiTokens;
  } catch (err) {
    toast.error(err instanceof ApiError ? err.message : 'Lecture des jetons impossible');
  } finally {
    chargement.value = false;
  }
}

// Le secret ne survit pas à la fermeture : rouvrir la fenêtre ne doit pas le
// remontrer à qui passerait derrière.
watch(
  () => props.open,
  (o) => {
    if (o) void charger();
    else cree.value = null;
  },
  { immediate: true },
);

async function creer(): Promise<void> {
  if (enCours.value || !nom.value.trim()) return;
  enCours.value = true;
  erreur.value = '';
  try {
    const res = await api.post<ApiTokenCreated>('/me/api-tokens', {
      name: nom.value.trim(),
      expiresInDays: duree.value === 'aucune' ? null : Number(duree.value),
    });
    cree.value = res;
    jetons.value = [res.apiToken, ...jetons.value];
    nom.value = '';
  } catch (err) {
    erreur.value = err instanceof ApiError ? err.message : 'Création impossible';
  } finally {
    enCours.value = false;
  }
}

async function copier(texte: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(texte);
    toast.success('Jeton copié');
  } catch {
    toast.error('Copie impossible : sélectionnez le jeton à la main');
  }
}

async function revoquer(j: ApiToken): Promise<void> {
  const ok = await confirmer({
    titre: 'Révoquer le jeton',
    message: `Le jeton « ${j.name} » cessera aussitôt de fonctionner.`,
    details: ['Tout outil qui l’utilise recevra « non authentifié » à son prochain appel.'],
    action: 'Révoquer',
  });
  if (!ok) return;
  try {
    await api.del(`/me/api-tokens/${j.id}`);
    jetons.value = jetons.value.filter((x) => x.id !== j.id);
    if (cree.value?.apiToken.id === j.id) cree.value = null;
    toast.success('Jeton révoqué');
  } catch (err) {
    toast.error(err instanceof ApiError ? err.message : 'Révocation impossible');
  }
}
</script>
